import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../store";
import { logout } from "../store/reducers/auth.reducer";
import { ROLES } from "../models/user.model";
import theme from "../themes";
import { ReactComponent as DashboardIcon } from "../assets/icons/sidebar/dashboard.svg";
import { ReactComponent as AgentIcon } from "../assets/icons/sidebar/agent.svg";
import { ReactComponent as InboundIcon } from "../assets/icons/sidebar/inbound.svg";
import { ReactComponent as OutboundIcon } from "../assets/icons/sidebar/outbound.svg";
import { ReactComponent as ReportIcon } from "../assets/icons/sidebar/report.svg";
import { ReactComponent as SettingIcon } from "../assets/icons/sidebar/setting.svg";
import { ReactComponent as ArrowDownIcon } from "../assets/icons/arrowDown.svg";
import logoImage from "../assets/images/logo.png";

const Sidebar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { t } = useTranslation();

  // Get user from localStorage as fallback
  const currentUser = user || JSON.parse(localStorage.getItem("user") || "{}");
  const userRole = currentUser.role;

  const navItems = [
    {
      label: t("navigation.dashboard"),
      icon: <DashboardIcon />,
      key: "dashboard",
      to: "/",
      adminRequired: false,
    },
    { 
      label: t("navigation.agents"), 
      icon: <AgentIcon />, 
      key: "agents", 
      to: "/agents" 
    },
    {
      label: t("navigation.inbound"),
      icon: <InboundIcon />,
      key: "inbound",
      to: "/inbound",
      adminRequired: false,
    },
    {
      label: t("navigation.outbound"),
      icon: <OutboundIcon />,
      key: "outbound",
      to: "/outbound",
      adminRequired: false,
    },
    {
      label: t("navigation.reports"),
      icon: <ReportIcon />,
      key: "reports",
      to: "/reports",
      adminRequired: false,
    },
    {
      label: t("navigation.settings"),
      icon: <SettingIcon />,
      key: "settings",
      to: "/settings",
      adminRequired: false,
    },
  ];

  // Filter navigation items based on user role
  const filteredNavItems = navItems.filter((item) => {
    if (item.adminRequired) {
      // Show admin-required items only if user role is not VIEW_ONLY (2)
      return userRole !== ROLES.VIEW_ONLY;
    }
    return true;
  });

  useEffect(() => {
    if (!menuOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  return (
    <SidebarContainer>
      <Logo>
        <LogoImage src={logoImage} alt="Healthcare Logo" />
        <LogoText>Lytte.ai</LogoText>
      </Logo>
      <Nav>
        {filteredNavItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.to}
            style={{ textDecoration: "none" }}
            end={item.to === "/"}
          >
            {({ isActive }) => (
              <NavItem selected={isActive}>
                <IconWrapper>
                  {item.icon}
                </IconWrapper>
                {item.label}
              </NavItem>
            )}
          </NavLink>
        ))}
      </Nav>
      <UserMenuContainer ref={menuRef}>
        <Avatar
          style={{ cursor: "pointer" }}
          onClick={() => setMenuOpen((v) => !v)}
        >
          {currentUser.full_name?.charAt(0)}
        </Avatar>
        <UserName>
          {currentUser.full_name}
        </UserName>
        <UserDropdown onClick={() => setMenuOpen((v) => !v)}>
          <ArrowDownIcon />
        </UserDropdown>
        {menuOpen && (
          <DropdownMenu>
            <DropdownItem onClick={() => navigate("/profile")}>
              {t("navigation.profile")}
            </DropdownItem>
            {userRole === ROLES.ADMIN && (
              <DropdownItem onClick={() => navigate("/user-management")}>
                {t("navigation.userManagement")}
              </DropdownItem>
            )}
            <DropdownItem onClick={handleLogout}>
              {t("auth.logout")}
            </DropdownItem>
          </DropdownMenu>
        )}
      </UserMenuContainer>
    </SidebarContainer>
  );
};

const SidebarContainer = styled.div`
  width: 240px;
  background: #fff;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #f0f0f0;
  padding: 32px 0;
  position: fixed;
  left: 0;
  top: 0;
  z-index: 100;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 32px 64px 32px;
`;

const LogoImage = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const LogoText = styled.h1`
  font-size: 20px;
  font-weight: 500;
  font-style: italic;
  margin: 0;
`;

const Nav = styled.nav`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 12px;
`;

const NavItem = styled.div<{ selected?: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  background: ${({ selected }) =>
    selected ? theme.colors.green2 : "transparent"};
  color: ${({ selected }) => (selected ? theme.colors.green1 : "#000")};
  font-size: 18px;
  font-weight: ${({ selected }) => (selected ? 500 : 400)};
  padding: 18px 22px;
  border-radius: 12px;
  cursor: pointer;
  width: 100%;

  svg {
    fill: ${({ selected }) => (selected ? theme.colors.green1 : "#000")} !important;
  }
  
  svg path {
    fill: ${({ selected }) => (selected ? theme.colors.green1 : "#000")} !important;
  }

  &:hover {
    background: #e8f6ef;
  }
`;

const IconWrapper = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #e8f6ef;
  color: #2ecc71;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 1.1rem;
`;

const UserName = styled.div`
  font-size: 1rem;
  color: #222;
  font-weight: 500;
`;

const UserDropdown = styled.div`
  margin-left: auto;
  color: #b0b0b0;
  cursor: pointer;
`;

const UserMenuContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 1rem 1rem 0rem 1rem;
  gap: 1rem;
`;

const DropdownMenu = styled.div`
  position: absolute;
  right: 16px;
  left: 16px;
  bottom: 48px;
  background: #fff;
  border: 1px solid #f0f0f0;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  min-width: 140px;
  z-index: 200;
`;

const DropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 16px;
  color: #222;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #e8f6ef;
  }
`;

export default Sidebar;
